import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import clientLogger from '../utils/clientLogger';

/**
 * Support Q/A Page Component
 * 
 * This page displays frequently asked questions and answers from the server.
 * Questions are fetched from the API and displayed in an expandable FAQ format.
 * 
 * @component
 * @returns {JSX.Element} Support Q/A page component
 */

const SupportQA = () => {
    // ================================
    // STATE MANAGEMENT
    // ================================

    const [faqData, setFaqData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedItems, setExpandedItems] = useState(new Set());

    // ================================
    // EFFECTS
    // ================================

    useEffect(() => {
        loadFAQData();
    }, []);

    // ================================
    // DATA LOADING
    // ================================

    /**
     * Loads FAQ data from the server
     */
    const loadFAQData = async () => {
        try {
            console.log('[SupportQA] Loading FAQ data from server...');
            setIsLoading(true);
            setError(null);

            const response = await apiService.getFAQ();

            if (response.ok) {
                const result = await response.json();

                if (result.success && result.data) {
                    // Sort FAQ items by position if available, otherwise by ID
                    const sortedData = result.data.sort((a, b) => {
                        if (a.position && b.position) {
                            return parseFloat(a.position) - parseFloat(b.position);
                        }
                        return a.id - b.id;
                    });

                    setFaqData(sortedData);
                    console.log('[SupportQA] FAQ data loaded successfully:', sortedData.length, 'items');
                } else {
                    throw new Error('Invalid response format from server');
                }
            } else {
                throw new Error(`Server responded with status: ${response.status}`);
            }
        } catch (error) {
            console.error('[SupportQA] Error loading FAQ data:', error);
            setError({
                type: 'data_loading',
                message: 'Failed to load FAQ data. Please try again later.',
                details: error
            });

            // Log the error for debugging
            clientLogger.logError('support_qa_load', `Failed to load FAQ data: ${error.message}`, error, {
                context: 'support_qa_data_loading'
            });
        } finally {
            setIsLoading(false);
        }
    };

    // ================================
    // EVENT HANDLERS
    // ================================

    /**
     * Toggles the expanded state of an FAQ item
     * @param {number} id - The ID of the FAQ item to toggle
     */
    const toggleExpanded = (id) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedItems(newExpanded);
    };

    /**
     * Handles retry loading FAQ data
     */
    const handleRetry = () => {
        setError(null);
        loadFAQData();
    };

    // ================================
    // RENDER HELPERS
    // ================================

    /**
     * Renders loading state
     * @returns {JSX.Element} Loading display
     */
    const renderLoading = () => {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 py-8">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                        <p className="text-white text-lg">Loading FAQ data...</p>
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Renders error state
     * @returns {JSX.Element} Error display
     */
    const renderError = () => {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 py-8">
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>

                            <h1 className="text-2xl font-bold text-gray-900 mb-2">
                                Unable to Load FAQ
                            </h1>

                            <p className="text-gray-600 mb-6">
                                {error.message}
                            </p>

                            <button
                                onClick={handleRetry}
                                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    /**
     * Renders FAQ item
     * @param {Object} item - FAQ item data
     * @param {number} index - Index of the item
     * @returns {JSX.Element} FAQ item component
     */
    const renderFAQItem = (item, index) => {
        const isExpanded = expandedItems.has(item.id);

        return (
            <div key={item.id} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                <button
                    onClick={() => toggleExpanded(item.id)}
                    className="w-full px-6 py-4 text-left focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-inset transition-colors duration-200 hover:bg-gray-50"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">
                                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                    {index + 1}
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-lg font-semibold text-gray-900 pr-4">
                                    {item.question}
                                </h3>
                            </div>
                        </div>
                        <div className="flex-shrink-0">
                            <svg
                                className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                                    }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                    </div>
                </button>

                {isExpanded && (
                    <div className="px-6 pb-4 border-t border-gray-100">
                        <div className="pt-4">
                            <div className="prose prose-gray max-w-none">
                                <div
                                    className="text-gray-700 leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                        __html: item.answer
                                            .replace(/\n/g, '<br>')
                                            .replace(/\r\n/g, '<br>')
                                            .replace(/\r/g, '<br>')
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    /**
     * Renders empty state when no FAQ data is available
     * @returns {JSX.Element} Empty state display
     */
    const renderEmptyState = () => {
        return (
            <div className="text-center py-12">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 mb-4">
                    <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No FAQ Available</h3>
                <p className="text-gray-500">There are currently no frequently asked questions available.</p>
            </div>
        );
    };

    // ================================
    // MAIN RENDER
    // ================================

    if (isLoading) {
        return renderLoading();
    }

    if (error) {
        return renderError();
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-white mb-4">
                        Support & FAQ
                    </h1>
                    <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                        Find answers to frequently asked questions about our registration process and features.
                    </p>
                </div>

                {/* FAQ Content */}
                <div className="space-y-4">
                    {faqData.length > 0 ? (
                        faqData.map((item, index) => renderFAQItem(item, index))
                    ) : (
                        renderEmptyState()
                    )}
                </div>

                {/* Contact Support Section */}
                <div className="mt-12 bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-8 text-center">
                    <div className="max-w-2xl mx-auto">
                        <h2 className="text-2xl font-bold text-white mb-4">
                            Still Need Help?
                        </h2>
                        <p className="text-purple-100 mb-6">
                            If you can't find the answer to your question, our support team is here to help.
                        </p>
                        <a
                            href="mailto:support@ourprysm.com"
                            className="inline-flex items-center px-6 py-3 bg-white text-purple-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Contact Support
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupportQA;
